import { theme as coreTheme, CoreTheme } from '@kizi/core-components';
import buttons from './variants/buttons';
import lists from './variants/lists';
import headings from './variants/headings';
import gradients from './gradients';
import baseColors from './baseColors';
import paragraphs from './variants/paragraphs';
import divs from './variants/divs';
import tables from './variants/tables';
import anchors from './variants/anchors';
import tableRows from './variants/tableRows';
import tableCells from './variants/tableCells';
import labels from './variants/labels';
import selects from './variants/selects';
import svgs from './variants/svgs';
import inputs from './variants/inputs';
import spans from './variants/spans';

//this is just to show you how to add variant groups
export type ComparatorVariantGroups = 'steps' | 'timeline';

type ComparatorTheme = CoreTheme<string> & {
  gradients: {
    [props: string]: any;
  };
};

export const baseSize = 16;

//this is just an example to show you how to copy all the keys in coreTheme.colors and redefine some keys
//this is just an example to show you how to copy all the keys in coreTheme.colors and redefine some keys
const colors = {
  ...baseColors,
  additionalInfo: baseColors.second[0],
  advice: baseColors.supplementary[1],
  alert: baseColors.supplementary[5],
  background: baseColors.main[0],
  danger: baseColors.supplementary[5],
  disabled: '#9B9B9B',
  disabledLight: baseColors.second[1],
  divider: baseColors.second[1],
  error: baseColors.supplementary[5],
  heading: baseColors.main[2],
  info: baseColors.main[1],
  placeholder: '#9B9B9B',
  primary: baseColors.main[1],
  rating: baseColors.main[2],
  secondary: baseColors.supplementary[4],
  success: baseColors.supplementary[2],
  tertiary: '',
  text: baseColors.main[2],
  textLight: baseColors.main[5],
  textAdvice: baseColors.dark[1],
  warning: baseColors.supplementary[1],
  white: baseColors.main[0],
  lightMain1: `${baseColors.main[1]}4D`,
  comparatorBG: '#F8F8F7',
};

export const breakpoints = ['425px', '768px', '1280px', '1440px', '2000px'];

export const fonts = {
  body: 'Inter, sans-serif',
  button: 'Inter, sans-serif',
  heading: 'Gilroy, Helvetica, Arial, sans-serif',
};

export const lineHeights = ['1.2', '1.4', '1.5'];

export const fontSizes = [11, 12, 14, 16, 18, 20, 36, 56].map(
  (px) => `${px ? px / baseSize : 0}rem`,
);

export const fontWeights = [400, 700];

export const mediaQueries = {};

export const radii = ['0px', '4px', '10px', '50px'];

export const shadows = {
  1: {
    boxShadow: `0px 1px 2px rgba(16, 24, 40, 0.05);`,
  },
  2: {
    boxShadow: `0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06);`,
  },
  3: {
    boxShadow: `0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06);`,
  },
  4: {
    boxShadow: `0px 12px 16px -4px rgba(16, 24, 40, 0.1), 0px 4px 6px -2px rgba(16, 24, 40, 0.05);`,
  },
  5: {
    boxShadow: `0px 20px 24px -4px rgba(16, 24, 40, 0.1), 0px 8px 8px -4px rgba(16, 24, 40, 0.04);`,
  },
  6: {
    boxShadow: `0px 24px 48px -12px rgba(16, 24, 40, 0.25);`,
  },
  7: {
    boxShadow: `0px 32px 64px -12px rgba(16, 24, 40, 0.2);`,
  },
  default: `0px 0px 20px rgba(0, 0, 0, 0.15);`,
};

export const space = [0, 5, 10, 15, 20, 25, 30, 50].map(
  (px) => `${px ? px / baseSize : 0}rem`,
);

export const zIndices = [0, 1, 2];

const themeOsSample: ComparatorTheme = {
  ...coreTheme,
  breakpoints,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  radii,
  shadows,
  space,
  gradients,
  variants: {
    anchors,
    buttons,
    divs,
    tables,
    tableRows,
    tableCells,
    headings,
    paragraphs,
    labels,
    lists,
    selects,
    svgs,
    inputs: {
      ...inputs,
      checkboxes: {
        main: {
          fontSize: 2,
          border: '1px solid',
          borderColor: 'second.0',
          borderRadius: '4px',
          '&:hover': {
            borderColor: 'main.1',
          },
          checked: {
            fontSize: 2,
            border: '2px solid',
            borderColor: 'main.1',
            borderRadius: '4px',
            '& input ~ span': {
              borderRadius: '0px',
            },
            '& input:checked ~ span svg': {
              fill: 'white',
            },
            '& input:checked ~ span': {
              bg: 'main.1',
            },
          },
          disabled: {
            fontSize: 2,
            border: '1px solid',
            borderColor: 'second.1',
            borderRadius: 1,
            '& input ~ span': {
              borderRadius: '2px',
            },
            '& input:checked ~ span svg': {
              fill: 'second.1',
            },
            background: '#F2F2F0',
          },
        },
      },
    },
    spans,
    steps: {
      active: {
        bg: 'main.2',
        borderRadius: 3,
      },
      activeText: {
        color: '#2B2F3B',
        fontFamily: 'body',
        lineHeight: 2,
        fontWeight: 500,
      },
      done: {
        color: '#12DE9A',
        fontFamily: 'body',
        lineHeight: 2,
      },
      inactive: {
        bg: 'second.0',
        borderRadius: 3,
      },
      inactiveText: {
        color: 'second.0',
        fontFamily: 'body',
        lineHeight: 2,
      },
      stepDone: {
        bg: '#12DE9A',
        borderRadius: 3,
      },
      stepNumber: {
        color: 'white',
        fontFamily: ['body', 'heading'],
        fontSize: [2, 2, 3],
        fontWeight: 1,
        lineHeight: 2,
      },
      verticalDash: {
        bg: 'second.1',
        height: [15, 30],
        my: [1, 3],
        ml: [2, 3],
        width: '1px',
      },
    },
    timeline: {
      dash: {
        bg: 'second.1',
        height: '100%',
        ml: 3,
        width: '1px',
      },
      dashWide: {
        bg: 'second.2',
        height: '100%',
        ml: '17px',
        width: '4px',
      },
      dashHorizontal: {
        position: 'absolute',
        top: 24,
        left: 4,
        bg: 'second.2',
        height: '2px',
        width: '90%',
      },
      item: {
        '&:not(:last-of-type)': {
          pb: 6,
        },
      },
      itemHorizontal: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      },
      marker: {
        bg: 'main.2',
        border: '1px solid main.2',
        borderRadius: 3,
        height: 30,
        width: 30,
      },

      markerNumber: {
        color: 'white',
        fontFamily: 'body',
        fontSize: 2,
        fontWeight: 1,
        lineHeight: 2,
      },
      textBlock: {
        ml: 3,
        '> p:first-of-type': {
          mt: 1,
        },
      },
      textBlockHorizontal: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '>p:first-of-type': {
          fontSize: 4,
        },
        '>p:last-of-type': {
          fontSize: 1,
        },
      },
    },
  },
  global: {
    body: {
      fontFamily: fonts.body,
    },
  },
  zIndices,
};

export default themeOsSample;
